import { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { ChartContainer } from "../ui/chart";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";
import { Zap, Activity, AlertTriangle, CheckCircle } from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { useZonePagination } from "../../../hooks/basic-reports/use-meter-pagination";
import MeterPagination from "./MeterPagination";
import ExportButton from "./ExportButton";
import { CHIP_COLORS } from "../utils/colors";
import { useReportData } from "../../../contexts/ReportDataContext";

const chartConfig = {
  voltage: { label: "Voltage (V)", color: CHIP_COLORS.info },
  frequency: { label: "Frequency (Hz)", color: CHIP_COLORS.success },
};

const ITEMS_PER_PAGE = 10;

const PowerQuality = () => {
  const { reports, loading, error, progress } = useReportData();
  const [showMeterTable, setShowMeterTable] = useState(true);
  const { getPage, setPage } = useZonePagination();

  if (loading)
    return (
      <div className="p-10 text-center">
        <div className="animate-pulse text-muted-foreground">
          {progress || "Loading Report Data..."}
        </div>
      </div>
    );
  if (error)
    return <div className="p-10 text-center text-destructive">{error}</div>;
  if (!reports)
    return <div className="p-10 text-center">No data available</div>;

  const { powerQuality } = reports;

  const voltageProfile = powerQuality.voltageProfile.map((d) => ({
    time: d.time,
    voltage: d.voltage,
    frequency: d.frequency,
    label: d.time,
  }));

  // Per-meter quality data
  const perMeterData = powerQuality.perMeter;

  // Pagination
  const currentPage = getPage("meters");
  const totalPages = Math.ceil(perMeterData.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, perMeterData.length);
  const paginatedMeters = perMeterData.slice(startIndex, endIndex);

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-info/10">
                <Zap className="w-4 h-4 text-info" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Avg Voltage</p>
                <div className="flex items-baseline gap-2">
                  <p className="text-lg font-bold">
                    {powerQuality.metrics.avgVoltage} V
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-success/10">
                <Activity className="w-4 h-4 text-success" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Avg Frequency</p>
                <p className="text-lg font-bold">
                  {powerQuality.metrics.avgFrequency} Hz
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-warning/10">
                <AlertTriangle className="w-4 h-4 text-warning" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">
                  Sag/Swell Events
                </p>
                <p className="text-lg font-bold">
                  {powerQuality.metrics.sagSwellEvents}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <CheckCircle className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">
                  Avg Power Factor
                </p>
                <p className="text-lg font-bold">
                  {powerQuality.metrics.avgPF}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-12 gap-6">
        <Card className="col-span-8">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base font-semibold">
                  Voltage Profile
                </CardTitle>
                <CardDescription>
                  Voltage fluctuations over time
                </CardDescription>
              </div>
              <Badge variant="outline" className="text-xs">
                Real-time
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-[280px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={voltageProfile}
                  margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                >
                  <defs>
                    <linearGradient
                      id="colorVoltage"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor={CHIP_COLORS.info}
                        stopOpacity={0.3}
                      />
                      <stop
                        offset="95%"
                        stopColor={CHIP_COLORS.info}
                        stopOpacity={0}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                    opacity={0.5}
                    vertical={false}
                  />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    minTickGap={30}
                  />
                  <YAxis
                    domain={["auto", "auto"]}
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    label={{
                      value: "Voltage (V)",
                      angle: -90,
                      position: "insideLeft",
                      fontSize: 11,
                      fill: "hsl(var(--muted-foreground))",
                    }}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        return (
                          <div className="bg-background border rounded-lg shadow-lg p-3">
                            <p className="font-medium text-xs mb-2">
                              {payload[0].payload.label}
                            </p>
                            <p className="text-sm font-medium">
                              {Number(payload[0].value).toFixed(1)} V
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="voltage"
                    stroke={CHIP_COLORS.info}
                    fillOpacity={1}
                    fill="url(#colorVoltage)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardContent>
        </Card>

        <Card className="col-span-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">
              Frequency Stability
            </CardTitle>
            <CardDescription>Grid frequency monitoring</CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-[280px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={voltageProfile}
                  margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    opacity={0.5}
                  />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    hide
                  />
                  <YAxis
                    domain={[48, 52]}
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        return (
                          <div className="bg-background border rounded-lg shadow-lg p-3">
                            <p className="text-sm font-medium">
                              {Number(payload[0].value).toFixed(2)} Hz
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="frequency"
                    stroke={CHIP_COLORS.success}
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardContent>
        </Card>
      </div>

      {/* Per-Meter Quality Table */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base font-semibold">
                Per-Meter Power Quality
              </CardTitle>
              <CardDescription>
                Detailed quality metrics for {perMeterData.length} meters
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <ExportButton reportName="Meter Quality Summary" />
              <Button
                variant="ghost"
                size="sm"
                className="h-8 text-primary"
                onClick={() => setShowMeterTable(!showMeterTable)}
              >
                {showMeterTable ? "Hide Details" : "Show Details"}
              </Button>
            </div>
          </div>
        </CardHeader>
        {showMeterTable && (
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Meter ID</TableHead>
                  <TableHead className="text-right">Avg Voltage</TableHead>
                  <TableHead className="text-right">Min / Max V</TableHead>
                  <TableHead className="text-right">Avg Freq</TableHead>
                  <TableHead className="text-right">Avg PF</TableHead>
                  <TableHead className="text-right">Under/Over Volt</TableHead>
                  <TableHead className="text-right">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedMeters.map((meter) => (
                  <TableRow key={meter.meterId} className="hover:bg-muted/50">
                    <TableCell className="font-mono text-sm">
                      {meter.meterId.replace(/^Demo1_/, "")}
                    </TableCell>
                    <TableCell className="text-right">
                      {meter.avgVoltage} V
                    </TableCell>
                    <TableCell className="text-right text-xs">
                      {meter.minVoltage} / {meter.maxVoltage}
                    </TableCell>
                    <TableCell className="text-right">
                      {meter.avgFrequency} Hz
                    </TableCell>
                    <TableCell className="text-right">{meter.avgPF}</TableCell>
                    <TableCell className="text-right">
                      <span className="text-muted-foreground">
                        {meter.underVoltEvents} / {meter.overVoltEvents}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge
                        variant="outline"
                        style={{
                          borderColor:
                            CHIP_COLORS[
                              meter.status as keyof typeof CHIP_COLORS
                            ] || CHIP_COLORS.good,
                          color:
                            CHIP_COLORS[
                              meter.status as keyof typeof CHIP_COLORS
                            ] || CHIP_COLORS.good,
                        }}
                      >
                        {meter.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <MeterPagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={perMeterData.length}
              startIndex={startIndex}
              endIndex={endIndex}
              onPageChange={(page) => setPage("meters", page)}
              hasNextPage={currentPage < totalPages}
              hasPrevPage={currentPage > 1}
            />
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default PowerQuality;
